/**
 * NotificationService - Handles Web Notifications API
 *
 * Manages permission requests and fires browser notifications
 * for important events like new chat messages.
 */

export type NotificationPermission = "granted" | "denied" | "default";

export type NotificationOptions = {
  title: string;
  body?: string;
  icon?: string;
  badge?: string;
  tag?: string;
  requireInteraction?: boolean;
  silent?: boolean;
  data?: any;
};

class NotificationService {
  private isSupported: boolean;

  constructor() {
    this.isSupported = typeof window !== "undefined" && "Notification" in window;
  }

  /**
   * Check if Web Notifications API is supported
   */
  isNotificationSupported(): boolean {
    return this.isSupported;
  }

  /**
   * Get current notification permission status
   */
  getPermission(): NotificationPermission {
    if (!this.isSupported) {
      return "denied";
    }
    return Notification.permission as NotificationPermission;
  }

  /**
   * Request notification permission from the user
   */
  async requestPermission(): Promise<NotificationPermission> {
    if (!this.isSupported) {
      console.warn("Notifications are not supported in this browser");
      return "denied";
    }

    if (Notification.permission === "granted") {
      return "granted";
    }

    if (Notification.permission === "denied") {
      return "denied";
    }

    try {
      const permission = await Notification.requestPermission();
      return permission as NotificationPermission;
    } catch (error) {
      console.error("Failed to request notification permission", error);
      return "denied";
    }
  }

  /**
   * Show a notification if permission is granted
   */
  async showNotification(options: NotificationOptions): Promise<boolean> {
    if (!this.isSupported) {
      console.warn("Notifications are not supported");
      return false;
    }

    // Auto-request permission if not yet determined
    if (Notification.permission === "default") {
      const permission = await this.requestPermission();
      if (permission !== "granted") {
        return false;
      }
    }

    if (Notification.permission !== "granted") {
      console.warn("Notification permission not granted");
      return false;
    }

    // Don't show notification if window is focused
    if (document.visibilityState === "visible" && document.hasFocus()) {
      return false;
    }

    try {
      const notification = new Notification(options.title, {
        body: options.body,
        icon: options.icon || "/icon-192.png",
        badge: options.badge,
        tag: options.tag,
        requireInteraction: options.requireInteraction ?? false,
        silent: options.silent ?? false,
        data: options.data
      });

      // Auto-close after 5 seconds unless requireInteraction is true
      if (!options.requireInteraction) {
        setTimeout(() => {
          notification.close();
        }, 5000);
      }

      // Focus window when notification is clicked
      notification.onclick = () => {
        window.focus();
        notification.close();
      };

      return true;
    } catch (error) {
      console.error("Failed to show notification", error);
      return false;
    }
  }

  /**
   * Show a notification for a new chat message
   */
  async notifyNewMessage(senderName: string, messageText: string, messageId: string): Promise<boolean> {
    return this.showNotification({
      title: `New message from ${senderName}`,
      body: messageText.length > 100 ? messageText.substring(0, 97) + "..." : messageText,
      tag: `message-${messageId}`,
      data: { type: "chat-message", messageId }
    });
  }
}

export const notificationService = new NotificationService();
